import React, { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Tiny π symbol that points to the secret arcade.
 *
 * Anti-scraper notes:
 *  - We never render an <a href="/arcade"> tag in the DOM. Bots that grep
 *    HTML for "/arcade" or follow every <a> tag won't find it.
 *  - The destination is built only at click time, from an obfuscated base64
 *    string, so the literal "arcade" string never appears in the parsed JSX.
 *  - The element is a <button type="button">, no aria-label that gives the
 *    page away.
 *
 * Behaviour:
 *  - Hidden on the landing page (/) — the homepage rail covers discovery there.
 *  - Every other route gets the π pinned to a viewport-edge spot (left/right
 *    strip), picked deterministically per pathname so it stays in a consistent
 *    place but never overlaps text, images or headers.
 *  - Triple-tap within 1.2s opens the codebreaker decoder modal (since mobile
 *    users can't easily type CREATE on a keyboard). A single tap navigates to
 *    the secret game.
 */

const POSITIONS = [
  "fixed top-[42%] right-1",
  "fixed top-[52%] left-1",
  "fixed top-[35%] right-2",
  "fixed top-[60%] left-2",
  "fixed top-[48%] right-1",
  "fixed top-[55%] left-1",
  "fixed top-[38%] left-2",
  "fixed top-[62%] right-2",
];

function spotForPath(pathname) {
  const key = (pathname || "/").toLowerCase();
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return POSITIONS[Math.abs(h) % POSITIONS.length];
}

// Base64 of "/arcade" — decoded at click time so the literal path never appears
// in the JSX source bots scrape from the bundle.
const SECRET_DEST = "L2FyY2FkZQ==";

function decodeDest() {
  try {
    return typeof atob === "function" ? atob(SECRET_DEST) : "/arcade";
  } catch {
    return "/arcade";
  }
}

export default function PiLauncher() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tapsRef = useRef([]);
  const navTimerRef = useRef(null);

  if (pathname === "/" || pathname === "") return null;
  if (
    pathname.startsWith("/arcade")
    || pathname.startsWith("/admin")
    || pathname.startsWith("/login")
    || pathname.startsWith("/register")
  ) return null;
  const posCls = spotForPath(pathname);

  const handleClick = (e) => {
    e.preventDefault();
    const now = Date.now();
    tapsRef.current = [...tapsRef.current.filter((t) => now - t < 1200), now];
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    if (tapsRef.current.length >= 3) {
      tapsRef.current = [];
      window.dispatchEvent(new CustomEvent("motowear:open-codebreaker"));
      return;
    }
    // Delay navigation so a follow-up tap can override into the codebreaker.
    navTimerRef.current = setTimeout(() => { navigate(decodeDest()); tapsRef.current = []; }, 380);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label=" "
      className={`${posCls} z-30 w-8 h-8 flex items-center justify-center text-[#FF3B30]/50 hover:text-[#FF3B30] hover:scale-110 font-display text-2xl leading-none transition-all select-none drop-shadow-sm bg-transparent border-0 cursor-pointer`}
      data-testid="pi-launcher"
    >
      π
    </button>
  );
}
